import { Fragment, useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } from 'react';
import { Layout, Form, Row, Col, Input, Button, Space, Tooltip, Select, Table, DatePicker, Flex, Card, theme } from 'antd';
import { filterFormItemLayout, filterFormGridLayout, filterFormItemStyle, computeButtonColLayout, fullPagerConfig } from '@utils/property';
import { useLocation, useNavigate } from 'react-router-dom';
import { get } from '@/core/request';
import dayjs from 'dayjs';
import PageHeader from '@components/PageHeader/PageHeader';
import { useHorizontalScrollGuard } from '@/hooks/useHorizontalScrollGuard';
import { useTableScrollY } from '@/hooks/useTableScrollY';
import { useVerticalScrollShadow } from '@/hooks/useVerticalScrollShadow';
import { useEnableTableScrollYLayout } from '@/layouts/tableScrollLayout';
import { useFilterPanelVisible } from '@/hooks/useFilterPanelVisible';

export default function BiChannelRegisterUsers() {
    const { token } = theme.useToken();
    const { filterToggleProps, FilterPanelSlot } = useFilterPanelVisible();
    const dateFormat = 'YYYY-MM-DD HH:mm:ss';
    const { RangePicker } = DatePicker;
    const [form1] = Form.useForm();
    const navigate = useNavigate();
    const [limit, setLimit] = useState(20);
    const [page, setPage] = useState(1);
    let [num, setNum] = useState(0);
    const location = useLocation();
    const pathname = location.state;
    const [falg, setFalg] = useState(true);
    const [dataSource, setDataSource] = useState(null);
    const [formData, setFormData] = useState({ startTime: '', endTime: '', memCard: '', memNickname: '', memMobile: '', memDeviceType: '', contentId: '', contentType: '', contentTitle: '' });
    const [start, setStart] = useState('');
    const [end, setEnd] = useState('');
    const [showElement, setShowElement] = useState(false);
    const [sourceType, setSourceType] = useState(null);

    const tableRegionRef = useRef(null);
    const tableScrollContainerRef = useRef(null);
    const [scrollContainerVersion, setScrollContainerVersion] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const tableDeductSelectors = useMemo(() => ['.bi-channel-reg-users-table-toolbar'], []);
    const tableScrollY = useTableScrollY(tableRegionRef, {
        enabled: enableTableScrollYLayout || isFullscreen,
        heightRootClassName: 'main-container-card',
        scrollYOffset: 0,
        deductSiblingSelectors: tableDeductSelectors,
    });

    useHorizontalScrollGuard(tableScrollContainerRef, {
        threshold: 6,
        edgeHoldMs: 220,
        watchValue: scrollContainerVersion,
    });
    useVerticalScrollShadow(tableScrollContainerRef, {
        threshold: 2,
        watchValue: scrollContainerVersion,
    });

    useLayoutEffect(() => {
        const region = tableRegionRef.current;
        if (!region) return;

        const resolveScrollContainer = () => {
            const next = region.querySelector('.ant-table-body') ?? region.querySelector('.ant-table-content');
            if (tableScrollContainerRef.current !== next) {
                tableScrollContainerRef.current = next;
                setScrollContainerVersion(v => v + 1);
            }
        };

        resolveScrollContainer();

        let rafId = 0;
        const mutationObserver = new MutationObserver(() => {
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                rafId = 0;
                resolveScrollContainer();
            });
        });
        mutationObserver.observe(region, { childList: true, subtree: true });

        return () => {
            if (rafId) cancelAnimationFrame(rafId);
            mutationObserver.disconnect();
        };
    }, []);

    const handleToggleFullscreen = useCallback(() => {
        const target = document.querySelector('main.main-container');
        if (!target) return;
        const next = !target.classList.contains('is-page-fullscreen');
        target.classList.toggle('is-page-fullscreen', next);
        setIsFullscreen(next);
    }, []);

    useEffect(() => {
        const onKeyDown = e => {
            if (e.key !== 'Escape') return;
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (!target) return;
            target.classList.remove('is-page-fullscreen');
            setIsFullscreen(false);
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, []);

    useEffect(() => {
        return () => {
            const target = document.querySelector('main.main-container.is-page-fullscreen');
            if (target) target.classList.remove('is-page-fullscreen');
        };
    }, []);

    useEffect(() => {
        if (pathname) {
            setStart(pathname.startTime);
            setEnd(pathname.endTime);
            setNum(++num);
        }
    }, []);

    const onReset = () => {
        form1.resetFields();
        form1.setFieldsValue({ time: '' });
        setFalg(false);
        setPage(1);
        setNum(++num);
        setFormData({ ...formData, startTime: '', endTime: '', memCard: '', memNickname: '', memMobile: '', memDeviceType: '', contentId: '', contentType: '', contentTitle: '' });
    };
    const columns = [
        { title: '用户唯一标识', dataIndex: 'memCard' },
        {
            title: '用户昵称',
            dataIndex: 'memNickname',
        },
        {
            title: '手机号',
            dataIndex: 'memMobile',
        },
        {
            title: '邮箱',
            dataIndex: 'memEmail',
        },
        {
            title: '注册时间',
            dataIndex: 'memRegistTime',
        },
        {
            title: '注册端口',
            dataIndex: 'memDeviceType',
        },
        {
            title: '最后登录时间',
            dataIndex: 'memLastestLoginTime',
        },
        {
            title: '转化来源类型',
            dataIndex: 'contentType',
        },
        {
            title: (
                <div>
                    实体ID&nbsp;
                    <Tooltip placement="top" title={'对应所选转化来源类型的实体ID，如选PGC文章时，即PGC文章ID'}>
                        <i className="bi bi-info-circle"></i>
                    </Tooltip>
                </div>
            ),
            dataIndex: 'contentId',
        },
        {
            title: '实体标题',
            dataIndex: 'contentTitle',
        },
        {
            title: '来源URL',
            dataIndex: 'url',
        },
    ];
    const clickBackHandler = () => {
        navigate('/operation/bi/channel/users');
    };
    const onFinish = values => {
        setFormData({ ...formData, memCard: values.memCard || '', memNickname: values.memNickname || '', memMobile: values.memMobile || '', memDeviceType: values.memDeviceType || '', contentType: values.contentType || '', contentTitle: values.contentTitle || '', contentId: values.contentId || '' });
        setNum(++num);
    };

    const handleChangeTime = (value, dateString) => {
        setFalg(false);
        setFormData({ ...formData, startTime: dateString[0], endTime: dateString[1] });
    };
    useEffect(() => {
        if (pathname && pathname.showTime) {
            form1.resetFields();
            form1.setFieldsValue({ time: '' });
            setFalg(false);
        }
    }, []);
    useEffect(() => {
        if (pathname) {
            const { startTime, endTime, memCard, memNickname, memMobile, memDeviceType, contentType, contentTitle, contentId } = formData;
            get(`/channel/statistics/regUser`, {
                channelId: pathname.channelId,
                startTime: falg ? pathname.startTime : startTime,
                endTime: falg ? pathname.endTime : endTime,
                memCard,
                memNickname,
                memDeviceType,
                page,
                limit,
                memMobile,
                contentType,
                contentTitle,
                contentId,
            }).then(
                res => {
                    if (res.status_code == 200) {
                        setDataSource(res.data);
                    }
                },
                err => {
                    console.log(err);
                },
            );
        }
    }, [limit, page, num]);
    useEffect(() => {
        get(`/channel/dictionary/source/type`).then(
            res => {
                if (res.status_code == 200) {
                    const arr = [];
                    for (var a in res.data) {
                        arr.push(res.data[a]);
                    }
                    setSourceType(arr);
                }
            },
            err => {
                console.log(err);
            },
        );
    }, []);
    const changeCurrPage = useCallback(page => {
        setPage(page);
    }, []);
    const changePageSize = useCallback(pageSize => {
        setLimit(pageSize);
    }, []);
    const paginationProps = {
        ...fullPagerConfig,
        showSizeChanger: { placement: 'topRight' },
        current: page,
        showTotal: () => `共${dataSource.totalCount}条记录`,
        pageSize: limit,
        size: 'small',
        total: dataSource && dataSource.totalCount,
        onShowSizeChange: (current, pageSize) => changePageSize(pageSize),
        onChange: page => {
            changeCurrPage(page);
        },
    };
    const onChangeSelect = value => {
        if (value) {
            setShowElement(true);
        } else {
            setShowElement(false);
        }
    };

    const onRefresh = () => {
        setNum(n => n + 1);
    };

    const channelName = pathname?.name ?? '';
    const channelId = pathname?.channelId ?? '';

    return (
        <div className="page-container bi-channel-reg-users-page">
            <PageHeader title={`渠道注册用户 ${channelName ? `[${channelName}]` : ''}`} breadcrumb={[{ label: '运营中心', path: '/operation/channels' }, { label: '渠道用户数据', path: '/operation/bi/channel/users' }, { label: '渠道注册用户' }]} {...filterToggleProps} />

            <FilterPanelSlot>
                <Card
                    size="small"
                    className="bi-channel-reg-users-filter"
                    style={{
                        marginBottom: token.marginLG,
                        border: `1px solid ${token.colorBorderSecondary}`,
                        background: token.colorFillAlter,
                    }}
                >
                    <Form {...filterFormItemLayout} onFinish={onFinish} form={form1} autoComplete="off" requiredMark={false}>
                        <Row gutter={[16, 16]}>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="用户唯一标识" name="memCard" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="用户唯一标识" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="用户昵称" name="memNickname" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="用户昵称" />
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="手机号" name="memMobile" style={filterFormItemStyle}>
                                    <Input allowClear placeholder="手机号" />
                                </Form.Item>
                            </Col>

                            <Col {...filterFormGridLayout}>
                                <Form.Item label="注册端口" name="memDeviceType" style={filterFormItemStyle}>
                                    <Select allowClear placeholder="注册端口">
                                        <Select.Option value={'pc'}>pc</Select.Option>
                                        <Select.Option value={'app'}>app</Select.Option>
                                        <Select.Option value={'wap'}>wap</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="注册时间" name="time" style={filterFormItemStyle}>
                                    {start && end && <RangePicker format={dateFormat} showTime onChange={handleChangeTime} defaultValue={[dayjs(start, dateFormat), dayjs(end, dateFormat)]} style={{ width: '100%' }} />}
                                </Form.Item>
                            </Col>
                            <Col {...filterFormGridLayout}>
                                <Form.Item label="转化来源类型" name="contentType" style={filterFormItemStyle}>
                                    <Select allowClear placeholder="转化来源类型" onChange={onChangeSelect}>
                                        {sourceType
                                            && sourceType.map((item, i) => {
                                                return <Select.Option value={i + 1}>{item}</Select.Option>;
                                            })}
                                    </Select>
                                </Form.Item>
                            </Col>
                            {showElement && (
                                <Col {...filterFormGridLayout}>
                                    <Form.Item label="实体ID" name="contentId" style={filterFormItemStyle} tooltip={{ title: '对应所选转化来源类型的实体ID，如选PGC文章时，即PGC文章ID', icon: <i className="bi bi-info-circle"></i> }}>
                                        <Input allowClear placeholder="转化来源实体ID" />
                                    </Form.Item>
                                </Col>
                            )}
                            {showElement && (
                                <Col {...filterFormGridLayout}>
                                    <Form.Item label="实体标题" name="contentTitle" style={filterFormItemStyle}>
                                        <Input allowClear placeholder="实体标题关键字" />
                                    </Form.Item>
                                </Col>
                            )}
                            <Col {...computeButtonColLayout(showElement ? 8 : 6)}>
                                <Form.Item label={<span>&nbsp;</span>} colon={false} style={filterFormItemStyle}>
                                    <Space size="small">
                                        <Button type="default" onClick={onReset}>
                                            重置
                                        </Button>
                                        <Button type="primary" icon={<i className="bi bi-search" aria-hidden="true" />} htmlType="submit">
                                            检索
                                        </Button>
                                    </Space>
                                </Form.Item>
                            </Col>
                        </Row>
                    </Form>
                </Card>
            </FilterPanelSlot>

            <div ref={tableRegionRef} className="bi-channel-reg-users-table-region">
                <div className="main-toolbar bi-channel-reg-users-table-toolbar">
                    <div className="main-tools">
                        <span className="table-title">渠道注册用户列表</span>
                    </div>
                    <div className="sub-tools">
                        <Space>
                            <Tooltip title="刷新列表">
                                <Button type="default" onClick={onRefresh} icon={<span className="bi bi-arrow-clockwise" aria-hidden="true" />} aria-label="刷新列表" />
                            </Tooltip>
                            <Tooltip title={isFullscreen ? '退出全屏' : '全屏'}>
                                <Button type="default" onClick={handleToggleFullscreen} icon={<span className={isFullscreen ? 'bi bi-fullscreen-exit' : 'bi bi-fullscreen'} aria-hidden="true" />} aria-label={isFullscreen ? '退出全屏' : '全屏'} />
                            </Tooltip>
                        </Space>
                    </div>
                </div>

                <div className="bi-channel-reg-users-table-wrap">
                    <Table
                        className={tableScrollY != null ? 'custom-table-scrollbar-always' : undefined}
                        rowKey="id"
                        columns={columns}
                        dataSource={dataSource?.list}
                        scroll={{
                            x: 'max-content',
                            ...(tableScrollY != null ? { y: tableScrollY } : {}),
                        }}
                        sticky
                        pagination={paginationProps}
                    />
                </div>
            </div>
        </div>
    );
}
